// Manager-portal page for editing a single Form. Mirrors the
// split-view pattern from AgencyProptreeSettingsPage: settings on the
// left, live iframe preview on the right. The preview iframe loads the
// landing app at /form/:id?preview=1 and receives postMessage patches
// whenever form state changes, so the manager sees unsaved edits
// without round-tripping through save.
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  FileText,
  GitBranch,
  GripVertical,
  HelpCircle,
  Languages,
  Plus,
  Star,
  Trash2,
  Undo2,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  Form,
  FormQuestion,
  FormQuestionOption,
  ProptreeEnumValue,
} from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { SectionCard } from '../components/SectionCard.js';
import { AppShell, useBannerSlot } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { Switch } from '../components/ui/switch.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { isTestMode } from '../lib/testMode.js';
import { client } from '../main.js';

// How the lead is qualified. Three choices:
//   none   — no verification; just collect answers.
//   2fa    — verify with a one-time code over WhatsApp / SMS / Email.
//   zapier — forward the completed submission to a Zapier webhook.
const QUALIFICATION_TYPE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'No qualification', value: 'none' },
  { id: 2, name: 'WhatsApp, Text and Email', value: '2fa' },
  { id: 3, name: 'Zapier', value: 'zapier' },
];

// Flow controls when 2FA verification happens. `questions_last`
// asks for the 2FA code first then runs through the questions;
// `questions_first` runs the questions, then 2FA (or Zapier submit)
// at the end. Public renderer reads `form.type.value` to drive the
// state machine.
const TYPE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: '2FA → Questions', value: 'questions_last' },
  { id: 2, name: 'Questions → 2FA', value: 'questions_first' },
];

// The channels a 2FA code can go out over.
const VERIFICATION_CHANNELS: {
  key: 'whatsapp' | 'sms' | 'email';
  label: string;
}[] = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'sms', label: 'Text' },
  { key: 'email', label: 'Email' },
];

const LANGUAGE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'English', value: 'en' },
  { id: 2, name: 'Swedish', value: 'sv' },
  { id: 3, name: 'Arabic', value: 'ar' },
  { id: 4, name: 'German', value: 'de' },
];

// Form-level pagination mode. Multi-page (default) is one question
// per page; single-page lays them out together with a single submit.
const LAYOUT_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Multi page', value: 'multi_page' },
  { id: 2, name: 'Single page', value: 'single_page' },
];

// Per-question render mode. `options` (default) is the historical
// radio-button behaviour; `dropdown` reuses the same options array
// but renders as a select; `input` / `text` collect free-form
// answers (no options); `multi` allows multiple options to be
// picked at once.
const ANSWER_TYPE_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Radio buttons', value: 'options' },
  { id: 2, name: 'Dropdown', value: 'dropdown' },
  { id: 3, name: 'Input field', value: 'input' },
  { id: 4, name: 'Text field', value: 'text' },
  { id: 5, name: 'Multi select', value: 'multi' },
];
// answerType values that use the options list. Other types (input,
// text) collect free-form input and don't render the options editor.
const OPTION_BASED_ANSWER_TYPES = new Set(['options', 'dropdown', 'multi']);

const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  'http://localhost:4040';

// === Form state =====================================================
interface FormState {
  name: string;
  questions: FormQuestion[];
  qualificationType: ProptreeEnumValue | null;
  type: ProptreeEnumValue | null;
  language: ProptreeEnumValue | null;
  layout: ProptreeEnumValue | null;
  /** Which channels a visitor may verify over. Only meaningful for the
   *  '2fa' qualification type. */
  verificationChannels: { whatsapp: boolean; sms: boolean; email: boolean };
  zapierWebhook: string;
}

function initialFormState(form: Form): FormState {
  // `qualificationType` and `layout` are new top-level fields added
  // by the new manager. Legacy forms won't have them set — that's
  // fine, the dropdowns fall back to their defaults.
  const qt = (form as { qualificationType?: ProptreeEnumValue | null })
    .qualificationType;
  const ly = (form as { layout?: ProptreeEnumValue | null }).layout;
  return {
    name: form.name ?? '',
    questions: Array.isArray(form.questions) ? form.questions : [],
    qualificationType: qt ?? null,
    type: form.type ?? null,
    language: form.language ?? null,
    layout: ly ?? null,
    // All three on when absent: that was the behaviour before the
    // switches existed, so an untouched legacy form keeps working.
    verificationChannels: (form as {
      verificationChannels?: {
        whatsapp?: boolean;
        sms?: boolean;
        email?: boolean;
      } | null;
    }).verificationChannels
      ? {
          whatsapp:
            (form as { verificationChannels?: { whatsapp?: boolean } })
              .verificationChannels?.whatsapp !== false,
          sms:
            (form as { verificationChannels?: { sms?: boolean } })
              .verificationChannels?.sms !== false,
          email:
            (form as { verificationChannels?: { email?: boolean } })
              .verificationChannels?.email !== false,
        }
      : { whatsapp: true, sms: true, email: true },
    zapierWebhook: form.zapierWebhook ?? '',
  };
}

// Random integer ids mirror the legacy editor
// (enterprise-app/src/components/createForm.js:2070-2080). Kept as
// Numbers because Lead.answeredQuestions keys on them — flipping to
// UUID strings would silently break the legacy join.
function newRandomId(): number {
  return Math.floor(Math.random() * 10 ** 13);
}

// === Page shell =====================================================
export function FormEditPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = React.useState<Form | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    if (!id) return;
    let alive = true;
    setError(null);
    setNotFound(false);
    setData(null);
    client.forms
      .get(id)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError((err as Error).message);
        }
      });
    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              {data?.name || 'Form'}
            </h1>
            <div className="mt-3 flex items-center gap-3">
              <Link
                to="/forms"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to forms
              </Link>
              <span className="text-muted-foreground/50">·</span>
              <div className="eyebrow">Forms / Edit</div>
            </div>
          </div>
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}

        {notFound && (
          <Card className="p-6">
            <p className="font-sans text-[13px] text-muted-foreground">
              No form with that id exists, or you don't have access to it.
            </p>
          </Card>
        )}

        {!data && !error && !notFound && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">
            Loading…
          </Card>
        )}

        {data && id && <SplitView formId={id} initial={data} />}
      </div>
    </AppShell>
  );
}

// === Split view ====================================================
function SplitView({ formId, initial }: { formId: string; initial: Form }) {
  const [form, setForm] = React.useState<FormState>(() =>
    initialFormState(initial),
  );
  const [original, setOriginal] = React.useState<FormState>(() =>
    initialFormState(initial),
  );
  // Undo stack mirrors the proptree pages — push pre-edit form, pop on
  // undo. Cleared on successful save.
  const [history, setHistory] = React.useState<FormState[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (JSON.stringify(form[key]) === JSON.stringify(value)) return;
    setHistory((h) => [...h, form]);
    setForm({ ...form, [key]: value });
  };

  // Zapier forwards the completed submission, so questions always run
  // first — the saved question order is forced to "Questions → 2FA"
  // (the dropdown already shows + locks this). Computed here rather than
  // mutated into live state so the qualification dropdown keeps using
  // the plain `set` path.
  const effectiveType =
    form.qualificationType?.value === 'zapier'
      ? (TYPE_OPTIONS.find((o) => o.value === 'questions_first') ?? form.type)
      : form.type;

  const handleUndo = () => {
    if (history.length === 0) return;
    const restored = history[history.length - 1];
    if (!restored) return;
    setForm(restored);
    setHistory(history.slice(0, -1));
  };
  const canUndo = history.length > 0;

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      // Only send fields that actually changed so the API leaves
      // sibling keys (theme, color, etc.) untouched.
      const patch: Record<string, unknown> = {};
      if (form.name !== original.name) patch.name = form.name;
      if (
        JSON.stringify(form.questions) !== JSON.stringify(original.questions)
      ) {
        patch.questions = form.questions;
      }
      if (
        JSON.stringify(form.qualificationType) !==
        JSON.stringify(original.qualificationType)
      ) {
        patch.qualificationType = form.qualificationType;
      }
      if (JSON.stringify(effectiveType) !== JSON.stringify(original.type)) {
        patch.type = effectiveType;
      }
      if (
        JSON.stringify(form.verificationChannels) !==
        JSON.stringify(original.verificationChannels)
      ) {
        patch.verificationChannels = form.verificationChannels;
      }
      if (JSON.stringify(form.language) !== JSON.stringify(original.language)) {
        patch.language = form.language;
      }
      if (JSON.stringify(form.layout) !== JSON.stringify(original.layout)) {
        patch.layout = form.layout;
      }
      if (form.zapierWebhook !== original.zapierWebhook) {
        patch.zapierWebhook = form.zapierWebhook || null;
      }
      if (Object.keys(patch).length > 0) {
        await client.forms.update(
          formId,
          patch as Parameters<typeof client.forms.update>[1],
        );
      }
      setOriginal(form);
      setHistory([]);
    } catch (err) {
      setSaveError(
        err instanceof ApiError ? err.message : (err as Error).message,
      );
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(original);

  return (
    <div className="space-y-4">
      {dirty && (
        <UnsavedChangesBanner
          onSave={handleSave}
          onUndo={handleUndo}
          canUndo={canUndo}
          saving={saving}
          error={saveError}
        />
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <SettingsForm form={form} set={set} />
          <DeleteFormCard formId={formId} />
        </div>
        <div className="lg:sticky lg:top-[calc(var(--header-h,4rem)+var(--banner-h,0px)+0.5rem)] lg:h-[calc(100dvh-var(--header-h,4rem)-var(--banner-h,0px)-1.5rem)]">
          <FormPreview formId={formId} form={form} />
        </div>
      </div>
    </div>
  );
}

// === Top sticky save/undo banner ====================================
function UnsavedChangesBanner({
  onSave,
  onUndo,
  canUndo,
  saving = false,
  error = null,
}: {
  onSave: () => void;
  onUndo: () => void;
  canUndo: boolean;
  saving?: boolean;
  error?: string | null;
}) {
  const slot = useBannerSlot();
  if (!slot) return null;
  return createPortal(
    <div className="border-b border-primary/40 bg-primary/10 text-foreground dark:bg-primary/20">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2 text-[13px]">
        <span className="flex-1">
          {error
            ? `Save failed: ${error}`
            : 'You have updated this form. Remember to save.'}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onUndo}
          disabled={!canUndo || saving}
          title="Undo last change"
        >
          <Undo2 />
          Undo
        </Button>
        {isTestMode() ? (
          <Tooltip
            label="Can't save form in test mode"
            className="cursor-not-allowed"
          >
            <Button type="button" size="sm" disabled>
              Save changes
            </Button>
          </Tooltip>
        ) : (
          <Button type="button" size="sm" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      </div>
    </div>,
    slot,
  );
}

// === Settings form (left column) ===================================
// Danger card at the bottom of the settings column. Mirrors the
// "New form" card on the list page (title + description + right-side
// action); the destructive action itself is confirmed in a modal.
function DeleteFormCard({ formId }: { formId: string }) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await client.forms.remove(formId);
      navigate('/forms');
    } catch (err) {
      setDeleting(false);
      setError((err as Error).message || 'Could not delete — try again.');
    }
  };

  return (
    <>
      <Card className="flex items-center gap-3 border-destructive/20 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="font-sans text-[13px] font-semibold leading-tight text-foreground">
            Delete form
          </div>
          <p className="mt-1 font-sans text-[11px] leading-snug text-muted-foreground">
            Permanently delete this form. Proptrees using it stop showing it.
          </p>
        </div>
        {isTestMode() ? (
          <Tooltip
            label="Can't delete form in test mode"
            className="cursor-not-allowed"
          >
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled
            >
              <Trash2 />
              Delete
            </Button>
          </Tooltip>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              setError(null);
              setConfirmOpen(true);
            }}
          >
            <Trash2 />
            Delete
          </Button>
        )}
      </Card>
      {confirmOpen && (
        <ConfirmDeleteFormDialog
          deleting={deleting}
          error={error}
          onCancel={() => {
            if (!deleting) setConfirmOpen(false);
          }}
          onConfirm={() => void handleDelete()}
        />
      )}
    </>
  );
}

// Confirm modal — same overlay/card convention as the other dialogs in
// the manager (e.g. the New-agent dialog): dimmed backdrop, Escape and
// backdrop-click to close, centered card.
function ConfirmDeleteFormDialog({
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  deleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete form"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <Card
        className="w-full max-w-[440px] p-0 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 pb-3 pt-4">
          <div className="font-sans text-[15px] font-semibold text-foreground">
            Delete this form?
          </div>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
            This permanently deletes the form and disconnects it from every
            proptree using it. Submitted inquiries are kept.
          </p>
        </div>
        {error && (
          <div className="mx-5 mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-[11px] text-destructive">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 px-5 py-4">
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={onConfirm}
            disabled={deleting}
          >
            <Trash2 />
            {deleting ? 'Deleting…' : 'Delete form'}
          </Button>
        </div>
      </Card>
    </div>,
    document.body,
  );
}

function SettingsForm({
  form,
  set,
}: {
  form: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}) {
  const updateQuestions = (next: FormQuestion[]) => set('questions', next);

  return (
    <div className="space-y-4">
      <SectionCard
        title="Identity"
        subtitle="Display name shown to managers and on shareable links"
        icon={FileText}
        defaultOpen
      >
        <div className="p-5">
          <Label htmlFor="form-name">Form name</Label>
          <Input
            id="form-name"
            className="mt-1"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="My qualifying form"
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Flow"
        subtitle="How visitors are qualified and when verification fires"
        icon={GitBranch}
        defaultOpen
      >
        <div className="grid grid-cols-12 gap-4 p-5">
          <div className="col-span-12 sm:col-span-6">
            <Label>Qualification type</Label>
            <Select
              className="mt-1"
              options={QUALIFICATION_TYPE_OPTIONS.map<SelectOption>((o) => ({
                value: String(o.value),
                label: o.name ?? '',
              }))}
              value={String(form.qualificationType?.value ?? '')}
              onChange={(v) =>
                set(
                  'qualificationType',
                  QUALIFICATION_TYPE_OPTIONS.find((o) => o.value === v) ?? null,
                )
              }
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              No qualification just collects answers. WhatsApp / Text /
              Email gate on a 2FA code. Zapier forwards the submission to
              the configured webhook.
            </p>
          </div>
          {form.qualificationType?.value === '2fa' && (
            // Independent switches, not a single choice: a brokerage
            // whose leads arrive on WhatsApp still wants email as a
            // fallback for anyone who doesn't use it.
            <div className="col-span-12">
              <Label>Verify over</Label>
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {VERIFICATION_CHANNELS.map(({ key, label }) => {
                  const on = form.verificationChannels[key];
                  // Never let the last one be switched off: 2FA with no
                  // channel can't send a code, so the form would collect
                  // nothing and give the visitor no way to continue.
                  const isLast =
                    on &&
                    Object.values(form.verificationChannels).filter(Boolean)
                      .length === 1;
                  return (
                    <div
                      key={key}
                      className="flex h-11 items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3.5 dark:border-white/10"
                    >
                      <Label
                        htmlFor={`channel-${key}`}
                        className="mb-0 min-w-0 truncate"
                      >
                        {label}
                      </Label>
                      <Switch
                        id={`channel-${key}`}
                        checked={on}
                        disabled={isLast}
                        onCheckedChange={(next) =>
                          set('verificationChannels', {
                            ...form.verificationChannels,
                            [key]: next,
                          })
                        }
                        aria-label={`Verify over ${label}`}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                The visitor picks one of these to receive their code. At
                least one has to stay on.
              </p>
            </div>
          )}
          <div className="col-span-12 sm:col-span-6">
            <Label>Question order</Label>
            <Select
              className="mt-1"
              disabled={
                form.qualificationType?.value === 'none' ||
                form.qualificationType?.value === 'zapier'
              }
              placeholder={
                form.qualificationType?.value === 'none' ? '—' : 'Select…'
              }
              options={TYPE_OPTIONS.map<SelectOption>((o) => ({
                value: String(o.value),
                label: o.name ?? '',
              }))}
              value={
                form.qualificationType?.value === 'none'
                  ? ''
                  : form.qualificationType?.value === 'zapier'
                    ? 'questions_first'
                    : String(form.type?.value ?? '')
              }
              onChange={(v) =>
                set('type', TYPE_OPTIONS.find((o) => o.value === v) ?? null)
              }
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              {form.qualificationType?.value === 'none'
                ? 'No verification, so question order doesn’t apply.'
                : form.qualificationType?.value === 'zapier'
                  ? 'Locked to Questions → 2FA — Zapier forwards the submission after the questions.'
                  : '2FA → Questions verifies first and gates the questions; Questions → 2FA asks first, then verifies before submit.'}
            </p>
          </div>
          {form.qualificationType?.value === 'zapier' && (
            <div className="col-span-12">
              <Label htmlFor="form-zapier">Zapier webhook URL</Label>
              <Input
                id="form-zapier"
                className="mt-1"
                value={form.zapierWebhook}
                onChange={(e) => set('zapierWebhook', e.target.value)}
                placeholder="https://hooks.zapier.com/hooks/catch/..."
              />
              <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                The submission is POSTed to this URL when the form
                completes. Leave blank to disable.
              </p>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Localisation"
        subtitle="Language the form is rendered in"
        icon={Languages}
      >
        <div className="p-5">
          <Label>Language</Label>
          <Select
            className="mt-1"
            options={LANGUAGE_OPTIONS.map<SelectOption>((o) => ({
              value: String(o.value),
              label: o.name ?? '',
            }))}
            value={String(form.language?.value ?? '')}
            onChange={(v) =>
              set(
                'language',
                LANGUAGE_OPTIONS.find((o) => o.value === v) ?? null,
              )
            }
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Questions"
        subtitle="Star an option to flag it as a qualifying answer"
        icon={HelpCircle}
        defaultOpen
      >
        <div className="border-b border-[hsl(var(--hairline))] p-5">
          <Label>Page layout</Label>
          <Select
            className="mt-1"
            options={LAYOUT_OPTIONS.map<SelectOption>((o) => ({
              value: String(o.value),
              label: o.name ?? '',
            }))}
            value={String(form.layout?.value ?? 'multi_page')}
            onChange={(v) =>
              set(
                'layout',
                LAYOUT_OPTIONS.find((o) => o.value === v) ?? null,
              )
            }
          />
          <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
            Multi-page shows one question at a time with a progress
            bar; single-page lays every question out on one screen
            with a single submit.
          </p>
        </div>
        <QuestionsEditor questions={form.questions} onChange={updateQuestions} />
      </SectionCard>
    </div>
  );
}

// === Questions list editor =========================================
function QuestionsEditor({
  questions,
  onChange,
}: {
  questions: FormQuestion[];
  onChange: (next: FormQuestion[]) => void;
}) {
  // Track which question rows are expanded. Default: collapsed. Newly
  // added rows auto-expand so the user can start typing immediately.
  // Keyed by the question's id (coerced to string for SortableContext
  // compatibility).
  const [openIds, setOpenIds] = React.useState<Set<string>>(new Set());

  const toggleOpen = (id: string) =>
    setOpenIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const addQuestion = () => {
    const id = newRandomId();
    onChange([
      ...questions,
      {
        id,
        question: '',
        options: [],
      },
    ]);
    setOpenIds((s) => new Set(s).add(String(id)));
  };
  const removeQuestion = (i: number) => {
    onChange(questions.filter((_, j) => j !== i));
  };
  const updateQuestion = (i: number, patch: Partial<FormQuestion>) => {
    onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  };

  // dnd-kit configuration. PointerSensor with a small activation
  // distance so a click on the row body (e.g. expanding) doesn't get
  // misinterpreted as a drag start.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = questions.findIndex((q) => String(q.id) === active.id);
    const newIndex = questions.findIndex((q) => String(q.id) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(questions, oldIndex, newIndex));
  };

  const itemIds = React.useMemo(
    () => questions.map((q) => String(q.id ?? '')),
    [questions],
  );

  return (
    <div className="space-y-3 p-5">
      {questions.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-6 text-center font-mono text-[11px] text-muted-foreground">
          No questions yet — add one to start qualifying leads.
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={itemIds}
          strategy={verticalListSortingStrategy}
        >
          {questions.map((q, i) => {
            const id = String(q.id ?? i);
            return (
              <SortableQuestionRow
                key={id}
                sortableId={id}
                index={i}
                question={q}
                isOpen={openIds.has(id)}
                onToggle={() => toggleOpen(id)}
                onChange={(patch) => updateQuestion(i, patch)}
                onRemove={() => removeQuestion(i)}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addQuestion}
      >
        <Plus className="h-4 w-4" />
        Add question
      </Button>
    </div>
  );
}

function SortableQuestionRow({
  sortableId,
  index,
  question,
  isOpen,
  onToggle,
  onChange,
  onRemove,
}: {
  sortableId: string;
  index: number;
  question: FormQuestion;
  isOpen: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<FormQuestion>) => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const options = Array.isArray(question.options) ? question.options : [];
  const updateOption = (i: number, patch: Partial<FormQuestionOption>) =>
    onChange({
      options: options.map((o, j) => (j === i ? { ...o, ...patch } : o)),
    });
  const removeOption = (i: number) =>
    onChange({ options: options.filter((_, j) => j !== i) });
  const addOption = () =>
    onChange({
      options: [
        ...options,
        { id: newRandomId(), title: '', qualification: false },
      ],
    });

  const titlePreview = question.question?.trim();

  return (
    <Card ref={setNodeRef} style={style} className="p-0">
      {/* Header bar — always visible. Click anywhere except the
          dedicated drag handle / delete button toggles expansion. */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <span
          ref={undefined}
          className="flex h-7 w-7 cursor-grab items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <Label className="shrink-0 cursor-pointer font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          Question {index + 1}
        </Label>
        <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
          {titlePreview || (
            <span className="text-muted-foreground/60 italic">
              Untitled
            </span>
          )}
        </span>
        <span
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          <ChevronDown
            className={
              'h-4 w-4 transition-transform ' + (isOpen ? 'rotate-180' : '')
            }
          />
        </span>
        <Button
          asChild
          type="button"
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title="Delete question"
        >
          <span>
            <Trash2 className="h-4 w-4" />
          </span>
        </Button>
      </button>

      {isOpen && (
        <div className="space-y-3 border-t border-[hsl(var(--hairline))] p-4">
          <Input
            value={question.question ?? ''}
            onChange={(e) => onChange({ question: e.target.value })}
            placeholder="What would you like to ask?"
          />
          <textarea
            value={question.information ?? ''}
            onChange={(e) => onChange({ information: e.target.value })}
            placeholder="Description (optional)"
            rows={2}
            className="flex min-h-[2.5rem] w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-sm transition-colors placeholder:text-muted-foreground/70 hover:border-[hsl(var(--border-strong))] focus:border-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:bg-card-raised dark:border-white/10"
          />
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              Answer type
            </Label>
            <Select
              className="mt-1"
              options={ANSWER_TYPE_OPTIONS.map<SelectOption>((o) => ({
                value: String(o.value),
                label: o.name ?? '',
              }))}
              value={String(question.answerType?.value ?? 'options')}
              onChange={(v) =>
                onChange({
                  answerType:
                    ANSWER_TYPE_OPTIONS.find((o) => o.value === v) ?? undefined,
                })
              }
            />
          </div>
          {OPTION_BASED_ANSWER_TYPES.has(
            String(question.answerType?.value ?? 'options'),
          ) && (
            <div className="space-y-2">
              <Label className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                Options
              </Label>
              {options.map((opt, i) => (
                <OptionRow
                  key={String(opt.id ?? i)}
                  option={opt}
                  onChange={(patch) => updateOption(i, patch)}
                  onRemove={() => removeOption(i)}
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addOption}
              >
                <Plus className="h-3 w-3" />
                Add option
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function OptionRow({
  option,
  onChange,
  onRemove,
}: {
  option: FormQuestionOption;
  onChange: (patch: Partial<FormQuestionOption>) => void;
  onRemove: () => void;
}) {
  const starred = option.qualification === true;
  return (
    <div className="flex items-center gap-2">
      <Input
        value={option.title ?? ''}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder="Option text"
      />
      <Button
        type="button"
        variant={starred ? 'default' : 'ghost'}
        size="icon"
        onClick={() => onChange({ qualification: !starred })}
        title={starred ? 'Qualifying answer' : 'Mark as qualifying'}
      >
        <Star className={'h-4 w-4 ' + (starred ? 'fill-current' : '')} />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        title="Delete option"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// === Right column — iframe preview =================================
interface FormPreviewPatch {
  name?: string | null;
  questions?: FormQuestion[];
  layout?: string | null;
  qualificationType?: string | null;
  verificationChannels?: {
    whatsapp: boolean;
    sms: boolean;
    email: boolean;
  } | null;
}
function buildFormPatch(form: FormState): FormPreviewPatch {
  // ProptreeEnumValue.value is typed as `string | number | boolean`
  // even though every option list uses string values. Coerce so the
  // postMessage payload stays JSON-clean.
  const layoutValue =
    typeof form.layout?.value === 'string' ? form.layout.value : null;
  const qualificationValue =
    typeof form.qualificationType?.value === 'string'
      ? form.qualificationType.value
      : null;
  return {
    name: form.name || null,
    questions: form.questions,
    layout: layoutValue,
    // Both needed for the preview to show the verification step, and to
    // show only the channels currently switched on — otherwise toggling
    // a switch changes nothing the manager can see.
    qualificationType: qualificationValue,
    verificationChannels: form.verificationChannels,
  };
}

function FormPreview({
  formId,
  form,
}: {
  formId: string;
  form: FormState;
}) {
  const url = `${PUBLIC_URL}/form/${formId}?preview=1`;
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const patch = React.useMemo(() => buildFormPatch(form), [form]);
  usePreviewBridge(iframeRef, patch);

  return (
    <Card className="flex h-full min-h-[640px] flex-col overflow-hidden p-0 lg:min-h-0">
      <div className="relative flex shrink-0 items-center justify-center border-b border-[hsl(var(--hairline))] px-5 py-3">
        <span className="mono-label absolute left-5 text-muted-foreground">
          Preview
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden bg-muted/40">
        <iframe
          ref={iframeRef}
          src={url}
          title="Form preview"
          className="h-full w-full border-0"
        />
      </div>
    </Card>
  );
}

// postMessage bridge — mirrors the proptree preview bridge. Re-posts
// the patch whenever it changes, and re-posts on the iframe's ready
// handshake (initial mount, internal nav).
function usePreviewBridge(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  patch: FormPreviewPatch,
) {
  const patchRef = React.useRef(patch);
  React.useEffect(() => {
    patchRef.current = patch;
  }, [patch]);

  React.useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'proptr:form-preview', patch },
      '*',
    );
  }, [iframeRef, patch]);

  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (
        d &&
        typeof d === 'object' &&
        d.type === 'proptr:form-preview-ready'
      ) {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'proptr:form-preview', patch: patchRef.current },
          '*',
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef]);
}
